/**
 * Admin Messages — suggestions in, notices out.
 *
 * An unanswered suggestion is highlighted, because a suggestion box nobody
 * replies to stops getting used.
 */

import { bibNo, timeAgo } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { NoticeSheet, ReplySheet } from '../../components/sheets/ClubSheets';
import { Button, ButtonRow, Empty, Eyebrow, Pill, ViewTitle } from '../../components/ui';

export function MessagesPage() {
  const { db } = useClubData();
  const { openSheet } = useUI();

  const suggestions = db.messages
    .filter((m) => m.kind === 'suggestion')
    .sort((a, b) => b.ts - a.ts);
  const notices = db.messages.filter((m) => m.kind === 'notice').sort((a, b) => b.ts - a.ts);

  return (
    <>
      <ViewTitle title="Messages" sub="Member suggestions in · club notifications out" />

      <div className="mb-[18px]">
        <ButtonRow>
          <Button
            tone="p"
            onClick={() => openSheet(<NoticeSheet memberCount={db.members.length} />)}
          >
            + Send notification to all members
          </Button>
        </ButtonRow>
      </div>

      <Eyebrow>Suggestions from members ({suggestions.length})</Eyebrow>
      {suggestions.length ? (
        suggestions.map((m) => {
          const from = db.members.find((x) => x.id === m.from);
          const replies = m.replies ?? [];

          return (
            // Unanswered suggestions get a faint brand tint so they read as "open".
            <div
              key={m.id}
              className={`mb-2.5 border border-line border-l-[3px] border-l-line-strong px-4 py-3.5 ${
                replies.length ? 'bg-white' : 'bg-gold-50/40'
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2.5">
                <div className="font-market text-[15px] font-bold tracking-[-0.015em]">
                  {from ? `${from.name} · bib ${bibNo(from.bib)}` : 'Member'}
                </div>
                <div>
                  <Pill tone="grey">Suggestion</Pill>{' '}
                  <span className="font-market text-[9.5px] text-copy-label">{timeAgo(m.ts)}</span>
                </div>
              </div>
              <div className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">{m.text}</div>

              {replies.map((r, i) => (
                <div
                  key={i}
                  className="mt-3 border border-line border-l-[3px] border-l-sea-600 bg-white px-4 py-3"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2.5">
                    <div className="font-market text-[15px] font-bold tracking-[-0.015em]">
                      You replied
                    </div>
                    <span className="font-market text-[9.5px] text-copy-label">{timeAgo(r.ts)}</span>
                  </div>
                  <div className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">{r.text}</div>
                </div>
              ))}

              {!replies.length && (
                <div className="mt-3 flex gap-2">
                  <Button
                    tone="g"
                    small
                    onClick={() =>
                      openSheet(
                        <ReplySheet
                          messageId={m.id}
                          originalText={m.text}
                          memberName={from?.name ?? 'member'}
                        />,
                      )
                    }
                  >
                    Reply
                  </Button>
                </div>
              )}
            </div>
          );
        })
      ) : (
        <Empty>No suggestions yet.</Empty>
      )}

      <Eyebrow>Notifications you&rsquo;ve sent</Eyebrow>
      {notices.length ? (
        notices.map((m) => (
          <div
            key={m.id}
            className="mb-2.5 border border-line border-l-[3px] border-l-gold-500 bg-white px-4 py-3.5"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2.5">
              <div className="font-market text-[15px] font-bold tracking-[-0.015em]">
                To all members
              </div>
              <div>
                <Pill tone="pend">Notice</Pill>{' '}
                <span className="font-market text-[9.5px] text-copy-label">{timeAgo(m.ts)}</span>
              </div>
            </div>
            <div className="mt-2 text-[13.5px] leading-relaxed text-copy-muted">{m.text}</div>
            <div className="mt-2 font-market text-[9.5px] tabular-nums text-copy-label">
              Read by {m.readBy.length} member(s)
            </div>
          </div>
        ))
      ) : (
        <Empty>Nothing sent yet.</Empty>
      )}
    </>
  );
}
